import { readableColor, transparentize } from 'polished';
import colors from '../../../styles/colors';
import { CSSProperties } from 'react';
import { vars } from '../../../theme';

export type ISideNavThemeProps = {
    backgroundColor?: CSSProperties['backgroundColor'];
    activeItemTextColor?: CSSProperties['color'];
    activeItemBackgroundColor?: CSSProperties['backgroundColor'];
    /**
     * Inactive item label color. Defaults to black or neutral400 picked for
     * contrast against `backgroundColor`; override when that automatic pick is
     * too harsh — pure black on a white rail, for example.
     */
    itemTextColor?: CSSProperties['color'];
    /**
     * Inactive item label color on hover/focus. Defaults to white on a dark
     * rail and to `itemTextColor` on a light one, so the label stays legible
     * against the hover fill either way.
     */
    itemTextColorHover?: CSSProperties['color'];
    /**
     * Color of the divider above the footer items. Defaults to a dark tone
     * suited to the default dark rail; override on a light rail.
     */
    dividerColor?: CSSProperties['color'];
};

export interface ISideNavTheme extends ISideNavThemeProps {
    activeItemBackgroundColorHover?: CSSProperties['backgroundColor'];
    activeItemBackgroundColorActive?: CSSProperties['backgroundColor'];
    itemBackgroundColorHover?: CSSProperties['backgroundColor'];
    itemBackgroundColorActive?: CSSProperties['backgroundColor'];
}

const defaultTheme = {
    backgroundColor: colors.neutral900,
    activeItemBackgroundColor: '#2B2C2D',
    activeItemTextColor: colors.white, // should be colors.neutral400
    dividerColor: '#2B2C2D'
};

const getHoverStates = (backgroundColor: string) => ({
    itemHover: transparentize(0.92, backgroundColor),
    itemActive: transparentize(0.88, backgroundColor),
    activeItemHover: transparentize(0.1, backgroundColor),
    activeItemActive: transparentize(0.2, backgroundColor)
});

export const useCreateTheme = (theme?: ISideNavThemeProps): ISideNavTheme => {
    const bg = theme?.backgroundColor || defaultTheme.backgroundColor;

    // Active item background color
    const activeItemBackgroundColor =
        theme?.activeItemBackgroundColor ||
        defaultTheme.activeItemBackgroundColor;

    // Active item Text color
    const activeItemTextColor =
        theme?.activeItemTextColor || defaultTheme.activeItemTextColor;

    // Active item color states
    const activeHoverStates = getHoverStates(activeItemBackgroundColor);
    const activeItemBackgroundColorHover = activeHoverStates.activeItemHover;
    const activeItemBackgroundColorActive = activeHoverStates.activeItemActive;

    // Item color states. Hover/active stay derived from the contrast-safe pick
    // even when the label color is overridden, so a pale override can't wash
    // the hover fill out to invisible. `readableColor` runs `strict` by default,
    // so on a mid-tone rail where neutral400 would land under AA it upgrades to
    // its own '#fff' — a plain light/dark branch cannot reproduce that.
    const contrastItemTextColor = readableColor(
        bg,
        colors.black,
        colors.neutral400
    );
    const itemTextColor = theme?.itemTextColor || contrastItemTextColor;
    const itemHoverStates = getHoverStates(contrastItemTextColor);
    const itemBackgroundColorHover = itemHoverStates.itemHover;
    const itemBackgroundColorActive = itemHoverStates.itemActive;

    // The hover fill is a wash of the label color, so on a light rail an
    // inverted (white) label lands white-on-near-white. Only invert when the
    // rail is actually dark; otherwise hover changes the fill and nothing else.
    // Asked with no candidate colors, `readableColor` is polished's own
    // light/dark verdict: its '#000'/'#fff' defaults always clear the contrast
    // check, so `strict` never swaps them and the answer is a plain flag rather
    // than a contrast pick like the call above. Keeping the cut-off in polished
    // avoids a hand-copied luminance threshold here.
    const isLightBackground = readableColor(bg) === '#000';
    const itemTextColorHover =
        theme?.itemTextColorHover ||
        (isLightBackground ? itemTextColor : vars.colors.text.invertedPrimary);

    // Divider color
    const dividerColor = theme?.dividerColor || defaultTheme.dividerColor;

    return {
        backgroundColor: bg,
        activeItemTextColor,
        activeItemBackgroundColor,
        activeItemBackgroundColorHover,
        activeItemBackgroundColorActive,
        itemTextColor,
        itemTextColorHover,
        itemBackgroundColorHover,
        itemBackgroundColorActive,
        dividerColor
    };
};

export default useCreateTheme;
